/**
 * Events Index — client-side search / sort / month / format / type filtering.
 *
 * The events listing renders its whole set server-side (App\Blocks\EventsIndex) and
 * filters here, over the rendered cards + archive rows, keyed off their `data-*`
 * attributes. That is the design's own interaction model (designs/events.html): the
 * upcoming grid gets month dividers inserted between cards and the past-events archive
 * filters in lockstep with it — neither of which the AJAX listing engine can express.
 * Event volume in a member portal is small, so one server render + instant client
 * filtering is both cheaper and flicker-free.
 *
 * Ported verbatim (behaviour-wise) from the inline script in the retired TEC template
 * override `tribe/events/v2/list.php`, now that the listing is a page-builder block.
 */
( function () {
	const ROOT = '.events-index-v3';

	// "2026-06" → "June 2026". Fixed English month names (the site language) so a
	// divider always matches the server-rendered month filter, whatever the browser
	// locale is.
	const MONTHS = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	];

	/**
	 * "2026-06" → "June 2026" ('' when the value isn't a Y-m string).
	 *
	 * @param {string} value A `Y-m` month key.
	 * @return {string} The human month label.
	 */
	const monthLabel = value => {
		const parts = ( value || '' ).split( '-' );

		if ( parts.length < 2 ) return '';

		return `${ MONTHS[ parseInt( parts[ 1 ], 10 ) - 1 ] || '' } ${ parts[ 0 ] }`;
	};

	/**
	 * The section-head count pill's copy: "4 of 9 events" (designs/events.html:1572).
	 *
	 * @param {number} visible How many rows survive the current filters.
	 * @param {number} total   How many exist in this section.
	 * @return {string} The pill label.
	 */
	const countLabel = ( visible, total ) =>
		`${ visible } of ${ total } ${ total === 1 ? 'event' : 'events' }`;

	/**
	 * Wire one events-index module.
	 *
	 * @param {HTMLElement} root The `.events-index-v3` block root.
	 */
	const initModule = root => {
		const form = root.querySelector( '.events-sidebar__form' );
		if ( ! form ) return;

		const grid = root.querySelector( '#fr-events-grid' );
		const archive = root.querySelector( '#fr-events-archive' );
		const empty = root.querySelector( '#fr-events-empty' );
		const upHead = root.querySelector( '#fr-events-upcoming-head' );
		const pastHead = root.querySelector( '#fr-events-past-head' );
		const upCount = root.querySelector( '#fr-events-upcoming-count' );
		const pastCount = root.querySelector( '#fr-events-past-count' );
		const search = root.querySelector( '#fr-events-search' );
		const sort = root.querySelector( '#fr-events-sort' );

		const cards = grid ? Array.from( grid.children ) : [];
		const rows = archive ? Array.from( archive.children ) : [];

		/**
		 * The current filter state, read from the rail.
		 *
		 * @return {Object} `{ search, month, format, type }`, all lowercase strings.
		 */
		const currentFilters = () => {
			const filters = {
				search: ( ( search && search.value ) || '' ).trim().toLowerCase(),
				month: '',
				format: '',
				type: '',
			};

			[ 'month', 'format', 'type' ].forEach( name => {
				const checked = form.querySelector( `input[name="${ name }"]:checked` );
				filters[ name ] = checked ? checked.value : '';
			} );

			return filters;
		};

		/**
		 * Does a card / archive row survive the current filters?
		 *
		 * @param {HTMLElement} el      A card or archive row.
		 * @param {Object}      filters The state from currentFilters().
		 * @return {boolean} True when the element should stay visible.
		 */
		const matches = ( el, filters ) => {
			if ( filters.month && el.getAttribute( 'data-month' ) !== filters.month ) return false;
			if ( filters.format && el.getAttribute( 'data-format' ) !== filters.format )
				return false;
			if ( filters.type && el.getAttribute( 'data-type' ) !== filters.type ) return false;

			if (
				filters.search &&
				( el.getAttribute( 'data-title' ) || '' ).indexOf( filters.search ) === -1
			) {
				return false;
			}

			return true;
		};

		/**
		 * The cards in the selected sort order (a copy — never mutates `cards`).
		 *
		 * @return {HTMLElement[]} The sorted cards.
		 */
		const sortedCards = () => {
			const mode = sort ? sort.value : 'upcoming';

			return cards.slice().sort( ( a, b ) => {
				if ( mode === 'az' || mode === 'za' ) {
					const cmp = ( a.getAttribute( 'data-title' ) || '' ).localeCompare(
						b.getAttribute( 'data-title' ) || ''
					);

					return mode === 'az' ? cmp : -cmp;
				}

				const da = a.getAttribute( 'data-date' ) || '';
				const db = b.getAttribute( 'data-date' ) || '';

				return mode === 'latest' ? db.localeCompare( da ) : da.localeCompare( db );
			} );
		};

		/**
		 * Build a month-divider element for the grid.
		 *
		 * @param {string} month The `Y-m` month key.
		 * @param {number} count How many visible events fall in it.
		 * @return {HTMLElement} The divider.
		 */
		const monthDivider = ( month, count ) => {
			const head = document.createElement( 'div' );

			head.className = 'events-month-head';
			head.innerHTML =
				`<h3 class="events-month-head__title">${ monthLabel( month ) }</h3>` +
				`<span class="events-month-head__count">${ count } ${
					count === 1 ? 'event' : 'events'
				}</span>`;

			return head;
		};

		/**
		 * Apply the current filters + sort: set visibility, re-append the grid in order
		 * with month dividers, update the counts, and hide any empty section.
		 */
		const apply = () => {
			const filters = currentFilters();
			const mode = sort ? sort.value : 'upcoming';
			const dateSort = mode === 'upcoming' || mode === 'latest';
			const sorted = sortedCards();

			// Clear the previous render's dividers.
			if ( grid ) {
				Array.from( grid.querySelectorAll( '.events-month-head' ) ).forEach( h =>
					h.remove()
				);
			}

			// Visibility + per-month visible counts (for the divider count).
			let visibleUp = 0;
			const monthCounts = {};

			sorted.forEach( card => {
				const show = matches( card, filters );

				card.hidden = ! show;

				if ( ! show ) return;

				visibleUp++;

				if ( dateSort ) {
					const month = card.getAttribute( 'data-month' ) || '';
					monthCounts[ month ] = ( monthCounts[ month ] || 0 ) + 1;
				}
			} );

			// Re-append in sorted order; on the date sorts insert a full-width month
			// divider before the first visible card of each month (mirrors the design).
			let currentMonth = null;

			sorted.forEach( card => {
				if ( ! grid ) return;

				if ( dateSort && ! card.hidden ) {
					const month = card.getAttribute( 'data-month' ) || '';

					if ( month !== currentMonth ) {
						currentMonth = month;
						grid.appendChild( monthDivider( month, monthCounts[ month ] || 0 ) );
					}
				}

				grid.appendChild( card );
			} );

			let visiblePast = 0;

			rows.forEach( row => {
				const show = matches( row, filters );

				row.hidden = ! show;

				if ( show ) visiblePast++;
			} );

			// "4 of 9 events" — the design's format (designs/events.html:1572-1573). The
			// visible-of-total shape is what makes the pill informative under a filter; a
			// bare count can't distinguish "9 events exist" from "9 matched".
			if ( upCount ) upCount.textContent = countLabel( visibleUp, cards.length );
			if ( pastCount ) pastCount.textContent = countLabel( visiblePast, rows.length );

			// Hide a section that has nothing in it — head AND container. The [hidden]
			// attribute needs the display:none rules in modules/_events-index.scss: these
			// containers are display:grid / flex.
			if ( upHead ) upHead.hidden = visibleUp === 0;
			if ( grid ) grid.hidden = visibleUp === 0;
			if ( pastHead ) pastHead.hidden = visiblePast === 0;
			if ( archive ) archive.hidden = visiblePast === 0;
			if ( empty )
				empty.classList.toggle( 'is-visible', visibleUp === 0 && visiblePast === 0 );
		};

		form.addEventListener( 'change', apply );
		form.addEventListener( 'input', apply );

		const clear = root.querySelector( '#fr-events-clear' );
		if ( clear ) {
			clear.addEventListener( 'click', () => {
				form.reset();
				apply();
			} );
		}

		const toggle = root.querySelector( '#fr-events-filter-toggle' );
		if ( toggle ) {
			toggle.addEventListener( 'click', () => {
				const open = form.classList.toggle( 'filters-open' );

				toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
			} );
		}

		apply();
	};

	document.addEventListener( 'DOMContentLoaded', () => {
		document.querySelectorAll( ROOT ).forEach( initModule );
	} );
} )();
